import React from 'react';
import { Box, Container, Grid, Typography, Card, CardContent } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { 
    TrendingUp, 
    FlashOn, 
    Security, 
    Public,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const AdvantageCard = ({ title, description, icon, color, isTall, isLarge }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return (
        <Card
            component={motion.div}
            whileHover={{ y: -5 }}
            sx={{
                height: '100%',
                borderRadius: '32px',
                background: isDark
                    ? `linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)`
                    : '#fff',
                border: '1px solid',
                borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: isDark ? 'none' : '0 10px 30px rgba(0,0,0,0.05)',
                '&:hover': {
                    borderColor: color,
                    background: isDark
                        ? `linear-gradient(135deg, ${color}10 0%, rgba(0,0,0,0) 100%)`
                        : `linear-gradient(135deg, ${color}05 0%, #fff 100%)`,
                    boxShadow: `0 20px 40px ${color}15`,
                }
            }}
        >
            <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box
                    sx={{
                        p: 1.5,
                        width: 'fit-content',
                        borderRadius: '12px',
                        background: `${color}20`,
                        color: color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3
                    }}
                >
                    {icon}
                </Box>
                <Typography variant={isLarge ? "h4" : "h5"} fontWeight={800} gutterBottom sx={{ color: theme.palette.text.primary, mb: isLarge ? 2 : 1 }}>
                    {title}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, fontSize: isLarge ? '1.1rem' : '1rem' }}>
                    {description}
                </Typography>
                
                {isTall && <Box sx={{ flexGrow: 1 }} />}
            </CardContent>
            
            {/* Background Decor */}
            <Box 
                sx={{ 
                    position: 'absolute', 
                    bottom: -20, 
                    right: -20, 
                    width: 100, 
                    height: 100, 
                    background: `radial-gradient(circle, ${color}15 0%, transparent 70%)`,
                    filter: 'blur(20px)',
                    zIndex: 0
                }} 
            />
        </Card>
    );
};

const AdvantagesSection = () => {
    const theme = useTheme();

    return (
        <Box id="advantages" sx={{ py: { xs: 8, md: 12 } }}>
            <Container maxWidth="lg">
                <Box sx={{ textAlign: "center", mb: 8 }}>
                    <Typography
                        variant="h2"
                        fontWeight={900}
                        gutterBottom
                        sx={{ 
                            color: theme.palette.text.primary,
                            fontSize: { xs: '2.2rem', md: '3.5rem' }
                        }}
                    >
                        Maximize Your <span style={{ 
                            background: 'linear-gradient(90deg, #00F0FF 0%, #00E676 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}>Impact</span>
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.2rem', maxWidth: '700px', mx: 'auto' }}>
                        Join the next-generation advertising ecosystem built for performance and transparency.
                    </Typography>
                </Box>

                <Grid container spacing={3}>
                    {/* Left Column (8/12) */}
                    <Grid item xs={12} md={8}>
                        <Grid container spacing={3}>
                            {/* Top row: Maximize Your Potential (Large) */}
                            <Grid item xs={12}>
                                <AdvantageCard
                                    title="Maximize Your Potential"
                                    description="Experience the full power of decentralized advertising rewards. Our platform offers unprecedented opportunities to grow your digital presence while getting rewarded for every engagement."
                                    icon={<TrendingUp sx={{ fontSize: 32 }} />}
                                    color="#00F0FF"
                                    isLarge
                                />
                            </Grid>
                            {/* Bottom row: Two small cards */}
                            <Grid item xs={12} sm={6}>
                                <AdvantageCard
                                    title="Blockchain Transparency"
                                    description="Every interaction is recorded on-chain, ensuring complete trust."
                                    icon={<Security sx={{ fontSize: 28 }} />}
                                    color="#7C4DFF"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <AdvantageCard
                                    title="Global Accessibility"
                                    description="Available anywhere in the world. All you need is internet."
                                    icon={<Public sx={{ fontSize: 28 }} />}
                                    color="#FF5252"
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Right Column (4/12) */}
                    <Grid item xs={12} md={4}>
                        <AdvantageCard
                            title="Zero Barrier to Entry"
                            description="No hidden costs. Start immediately with just your account or wallet. We've removed all friction to make your entry into Web3 advertising as seamless as possible. Experience a platform where innovation meets simplicity."
                            icon={<FlashOn sx={{ fontSize: 32 }} />}
                            color="#00E676"
                            isTall
                        />
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default AdvantagesSection;
